import { AfterViewInit, ChangeDetectionStrategy, Component, DestroyRef, ElementRef, OnDestroy, inject, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { from, take } from 'rxjs';
import { createDigitaServiceError } from 'src/app/app-error';
import { MediaRepository } from '../media.repository';
import { MediaService } from '../media.service';

/**
 * The Media Vimeo Provider Component.
 *
 * It is not standalone and belongs within the {@link MediaComponent}.
 *
 * See {@link https://developer.vimeo.com/api/guides/start}
 */
@Component({
  selector: 'app-media-vimeo',
  templateUrl: './media-vimeo.component.html',
  styleUrls: ['./media-vimeo.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MediaVimeoComponent implements AfterViewInit, OnDestroy {
  protected readonly mediaRepository = inject(MediaRepository);
  private readonly mediaService = inject(MediaService);
  private readonly destroyRef = inject(DestroyRef);

  /**
   * A reference to the div the Vimeo API converts into the player iframe.
   */
  private readonly mediaElement = viewChild.required<ElementRef<HTMLDivElement>>('media');

  /**
   * Holds a reference to the vimeo player
   */
  private player?: Vimeo.Player;

  /////////////////////////////////////////////////////////////////////
  // LIFECYCLE
  /////////////////////////////////////////////////////////////////////

  /**
   * Lifecycle
   */
  ngAfterViewInit() {
    // this component only renders once the media is initialized, so the settings are guaranteed to exist
    const options = this.mediaRepository.vimeoSettings();
    if (!options) {
      throw createDigitaServiceError('MediaVimeoComponent', 'ngAfterViewInit', 'No Vimeo settings available.', 'internal');
    }
    this.createPlayer(options);
  }

  /**
   * Lifecycle
   */
  ngOnDestroy() {
    this.player?.destroy();
  }

  /////////////////////////////////////////////////////////////////////
  // PLAYER CREATION
  /////////////////////////////////////////////////////////////////////

  /**
   * Create the player.
   *
   * @param options - the vimeo options
   */
  private createPlayer(options: Vimeo.Options) {
    // create the player
    this.player = new Vimeo.Player(this.mediaElement().nativeElement, options);

    // listen to events on the player
    this.player.on('loaded', () => {
      // once the player is ready we will find the total time of the video
      this.updateDuration();

      // inform the store that the video is ready
      this.mediaService.applyReady();
    });

    this.player.on('ended', () => {
      this.videoEnded();
    });

    this.player.on('play', () => {
      this.videoPlaying();
    });

    this.player.on('pause', () => {
      this.videoPaused();
    });

    this.player.on('error', (event) => {
      this.onError(event);
    });

    this.player.on('timeupdate', (event) => {
      this.updateCurrentTime(event);
    });

    // create the integration
    this.createPlayerIntegration();
  }

  /**
   * Integrates the player with the MediaService of the application.
   */
  private createPlayerIntegration() {
    // listen for play events.
    this.mediaService.play$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.player?.play();
    });

    // listen for pause events.
    this.mediaService.pause$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.player?.pause();
    });

    // seek to the current time
    this.mediaService.seekTo$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      if (data) {
        this.player?.setCurrentTime(data.seconds);
        if (data.play) {
          this.player?.play();
        } else {
          this.player?.pause();
        }
      }
    });

    // mute the media
    this.mediaService.mute$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.player?.setMuted(true);
    });

    // unMute the media
    this.mediaService.unMute$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.player?.setMuted(false);
    });

    // set the volume
    this.mediaService.volume$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((volume) => {
      if (typeof volume === 'number' && volume >= 0 && volume <= 100) {
        this.player?.setVolume(volume / 100);
      }
    });
  }

  /**
   * The Video has ended.
   */
  private videoEnded() {
    if (this.player) {
      from(this.player.getCurrentTime())
        .pipe(take(1), takeUntilDestroyed(this.destroyRef))
        .subscribe((currentTime) => {
          this.mediaService.applyCurrentTime(currentTime);
        });
    }
    this.mediaService.applyEnded();
  }

  /**
   * Occurs when the video is playing.
   */
  private videoPlaying() {
    this.mediaService.applyPlaying();
  }

  /**
   * Occurs when the video is paused.
   */
  private videoPaused() {
    this.mediaService.applyPaused();
  }

  /////////////////////////////////////////////////////////////////////
  // UTILS
  /////////////////////////////////////////////////////////////////////

  /**
   * Save the current time reported by the player to the store.
   */
  private updateCurrentTime(event: Vimeo.TimeEvent) {
    this.mediaService.applyCurrentTime(Math.floor(event.seconds));
  }

  /**
   * Retrieve the total duration from the player and save it to the store.
   */
  private updateDuration() {
    if (this.player) {
      from(this.player.getDuration())
        .pipe(take(1), takeUntilDestroyed(this.destroyRef))
        .subscribe((duration) => {
          this.mediaService.applyDuration(Math.floor(duration));
        });
    }
  }

  /**
   * Occurs when an error occurs.
   *
   * @param event - the event
   */
  private onError(event: Vimeo.Error) {
    this.mediaService.applyError(event?.message);
  }

  /**
   * Occurs on pointer up from the touch div when a Vimeo has no controls.
   */
  controlZonePointerUp() {
    if (this.player) {
      this.player.getPaused().then((paused) => {
        if (paused) {
          this.player?.play();
        } else {
          this.player?.pause();
        }
      });
    }
  }
}
